import axios, { type AxiosError } from 'axios';
import type { AlternativeProductItemType } from '../../types/api/AlternativeProductsApiType';
import type { Products } from '../../api/graphql/products/types';
import type { Product } from '../../hooks/use-product-cart';
import { getAlternativeProducts } from '../getAlternativeProducts';
import { extractProductId } from './utils';

/**
 * hasNextPage が false になるまでページを進めて検索する（網羅）。
 * 万一 API が hasNextPage を常に true のまま返す場合の無限ループ防止だけ、この回数で打ち切る。
 * （10件/ページ想定で約 2 万件分）
 */
const SAFETY_MAX_PAGES = 2000;

const MESSAGE_ADD_PRODUCT_RETRY =
  '商品を追加できませんでした。\n時間をおいて再度お試しください。';

/**
 * getAlternativeProducts が返す「1ページ分」の products を走査する。
 *
 * API は商品（product）単位の配列で、各要素に variants（バリエーション一覧）がぶら下がっている。
 * ここではその入れ子をフラットにし、各 variant の variantId を Set に積む。
 * ページを進めるたびに呼び出し、同じ Set にどんどん足していくことで「これまで取得した全ページ分の
 * 変更可能 variantId の集合」を表す。
 */
function addPageProductsToVariantSet(products: AlternativeProductItemType[], into: Set<string>): void {
  for (const prod of products) {
    for (const v of prod.variants) {
      into.add(v.variantId);
    }
  }
}

/**
 * 同じ sellingPlanId で「契約に追加しようとしている」variantId の集合 required に対し、
 * API からこれまで集めた候補の集合 container に、required の要素がすべて含まれるかを見る。
 *
 * 1つでも container に無い variantId があれば false（まだ次ページを取る必要がある、または最終的に不許可）。
 * すべて含まれていれば true（このプランについては追加予定分はすべて変更可能一覧に載っている）。
 * Set を for-of せず Array.from しているのは、ビルドターゲット都合で Set のイテレーションを避けるため。
 */
function setContainsAllVariantIds(container: Set<string>, required: Set<string>): boolean {
  const requiredList = Array.from(required);
  for (let i = 0; i < requiredList.length; i++) {
    if (!container.has(requiredList[i])) return false;
  }
  return true;
}

/**
 * sellingPlanId 単位で getAlternativeProducts をページングし、
 * requiredVariantIds のすべてが候補に含まれるか検証する。
 * すべて見つかった時点で次ページは取らない（早期終了）。
 */
async function verifySellingPlanAgainstAlternatives(
  token: string,
  sellingPlanId: string,
  requiredVariantIds: Set<string>
): Promise<boolean> {
  if (requiredVariantIds.size === 0) return true;

  const collected = new Set<string>();
  let page = 0;
  while (page < SAFETY_MAX_PAGES) {
    const res = await getAlternativeProducts(token, sellingPlanId, page);
    addPageProductsToVariantSet(res.products, collected);
    if (setContainsAllVariantIds(collected, requiredVariantIds)) {
      return true;
    }
    if (!res.hasNextPage) return false;
    page += 1;
  }
  console.error(
    'verifySellingPlanAgainstAlternatives: SAFETY_MAX_PAGES に達しました。API の hasNextPage を確認してください。'
  );
  return false;
}

/**
 * 「今回の定期の変更」のうち、もともと数量0だった商品を1以上にした行だけを対象に、
 * 変更可能商品一覧へ載っているか確認する（updateSubscriptionV2 を送る前のチェック用）。
 *
 * sellingPlanId ごとに getAlternativeProducts をまとめて呼び、同一プランの複数追加では
 * ページングを重ねない（必要 variant が揃ったら打ち切り）。プランが複数あるときは並列検証する。
 */
export async function verifyCreatesAgainstAlternativeProducts(
  token: string,
  products: Products,
  create: Product[]
): Promise<{ ok: true } | { ok: false; message: string }> {
  const requiredByPlan = new Map<string, Set<string>>();

  for (const p of create) {
    const productKey = extractProductId(p.productId) ?? p.productId;
    const row = products[productKey];
    if (!row) {
      return { ok: false, message: '商品情報が見つかりませんでした' };
    }
    if (!p.sellingPlanId) {
      return { ok: false, message: 'プラン情報が不足しています' };
    }
    let ids = requiredByPlan.get(p.sellingPlanId);
    if (!ids) {
      ids = new Set<string>();
      requiredByPlan.set(p.sellingPlanId, ids);
    }
    ids.add(row.variantId);
  }

  try {
    const checks = await Promise.all(
      Array.from(requiredByPlan.entries()).map(([sellingPlanId, variantIds]) =>
        verifySellingPlanAgainstAlternatives(token, sellingPlanId, variantIds).then((allowed) => ({
          sellingPlanId,
          allowed,
        }))
      )
    );
    for (const { allowed } of checks) {
      if (!allowed) {
        return { ok: false, message: MESSAGE_ADD_PRODUCT_RETRY };
      }
    }
  } catch (raw: unknown) {
    if (!axios.isAxiosError(raw)) {
      if (raw instanceof Error) throw raw;
      throw new Error(String(raw));
    }
    const axErr = raw as AxiosError;
    if (axErr.response != null) {
      return {
        ok: false,
        message: MESSAGE_ADD_PRODUCT_RETRY,
      };
    }
    throw axErr;
  }

  return { ok: true };
}
